import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { editDeviceReducer } from './editDeviceReducer';
import {
	EditDeviceContext,
	editDeviceInitialState,
	TEditDeviceSelectedItem,
	TTypeOfModalWindow,
} from './editDeviceContext';
import { EditDeviceActionTypes } from './editDeviceActions';
import {
	IItemForGetShortInfo,
	IServerError,
	IDeviceHardwareIdValidationError,
	IDeviceBankSettingForEdit,
	IEditDevice,
	IGetDeviceInfo,
	IRestaurantBankSettings,
	IGetRestaurantTlsCertificateInfo,
} from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getDeviceInfo } from 'backend/devices';
import { getRestaurantTlsCertificateInfo } from 'backend/restaurants';

const EditDeviceState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editDeviceReducer, editDeviceInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [isFirstSetCertificateName, setIsFirstSetCertificateName] = useState(true);

	const params = new URL(document.location.href).searchParams;
	const deviceID = params.get('deviceID');

	const {
		response: deviceResponse,
		loading: deviceLoading,
		refetch: deviceRefetch,
	} = useFetchData<IGetDeviceInfo, string>(deviceID ? getDeviceInfo : null, deviceID || '');

	const {
		response: tlsCertificateInfoResponse,
		loading: tlsCertificateInfoLoading,
		refetch: tlsCertificateInfoRefetch,
	} = useFetchData<IGetRestaurantTlsCertificateInfo, string>(
		state.IDSelectedRestaurant ? getRestaurantTlsCertificateInfo : null,
		state.IDSelectedRestaurant || '',
	);

	useEffect(() => {
		if (state.needRefetchData) {
			deviceRefetch();
			dispatch({
				type: EditDeviceActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		deviceLoading || tlsCertificateInfoLoading ? showLoader() : hideLoader();
	}, [deviceLoading, tlsCertificateInfoLoading]);

	useEffect(() => {
		if (deviceResponse) {
			!deviceResponse.data.tlsCertificateName && setIsFirstSetCertificateName(false);

			dispatch({
				type: EditDeviceActionTypes.SET_ALL_DATA,
				payload: {
					deviceId: deviceResponse.data.id,
					hardwareId: deviceResponse.data.hardwareId,
					IDSelectedCustomer: deviceResponse.data.customerId,
					IDSelectedRestaurant: deviceResponse.data.restaurantId,
					bankSettings: deviceResponse.data.deviceBankSettings.map((setting) => ({
						...setting,
						m4BankSettingCredentials: setting.m4BankSettingCredentials
							? {
									...setting.m4BankSettingCredentials,
									isPasswordUpdated: false,
							  }
							: {
									login: null,
									password: null,
									isPasswordUpdated: false,
							  },
						toucanBankSettingCredentials: setting.toucanBankSettingCredentials
							? {
									...setting.toucanBankSettingCredentials,
									isPasswordUpdated: false,
							  }
							: {
									login: null,
									password: null,
									isPasswordUpdated: false,
							  },
					})),
					availableRestaurantBankSettings: deviceResponse.data.availableRestaurantBankSettings,
					tlsCertificateName: deviceResponse.data.tlsCertificateName,
					tlsCertificateFilePath: deviceResponse.data.tlsCertificateFilePath,
					tlsCertificateFileId: '',
				},
			});
			dispatch({
				type: EditDeviceActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						hardwareId: deviceResponse.data.hardwareId,
						IDSelectedCustomer: deviceResponse.data.customerId,
						IDSelectedRestaurant: deviceResponse.data.restaurantId,
						bankSettings: deviceResponse.data.deviceBankSettings.map((setting) => ({
							...setting,
							m4BankSettingCredentials: setting.m4BankSettingCredentials
								? {
										...setting.m4BankSettingCredentials,
										isPasswordUpdated: false,
								  }
								: {
										login: null,
										password: null,
										isPasswordUpdated: false,
								  },
							toucanBankSettingCredentials: setting.toucanBankSettingCredentials
								? {
										...setting.toucanBankSettingCredentials,
										isPasswordUpdated: false,
								  }
								: {
										login: null,
										password: null,
										isPasswordUpdated: false,
								  },
						})),
						tlsCertificateName: deviceResponse.data.tlsCertificateName,
						tlsCertificateFilePath: deviceResponse.data.tlsCertificateFilePath,
						tlsCertificateFileId: '',
					},
				},
			});
		}
	}, [deviceResponse]);

	useEffect(() => {
		if (tlsCertificateInfoResponse) {
			isFirstSetCertificateName && setIsFirstSetCertificateName(false);

			dispatch({
				type: EditDeviceActionTypes.SET_TLS_CERTIFICATE_LEVEL,
				payload: {
					tlsCertificateLevel: tlsCertificateInfoResponse.data.tlsCertificateLevel,
					tlsCertificateName: isFirstSetCertificateName
						? state.tlsCertificateName
						: tlsCertificateInfoResponse.data.tlsCertificateName,
					tlsCertificateFilePath:
						tlsCertificateInfoResponse.data.tlsCertificateLevel === 'Device'
							? state.tlsCertificateFilePath
							: null,
					tlsCertificateFileId:
						tlsCertificateInfoResponse.data.tlsCertificateLevel === 'Device'
							? state.tlsCertificateFileId
							: null,
					tlsCertificateFileHash:
						tlsCertificateInfoResponse.data.tlsCertificateLevel === 'Device'
							? state.tlsCertificateFileHash
							: null,
				},
			});
		}
	}, [tlsCertificateInfoResponse]);

	useEffect(() => {
		if (state.IDSelectedRestaurant) {
			tlsCertificateInfoRefetch();
		} else {
			dispatch({
				type: EditDeviceActionTypes.SET_TLS_CERTIFICATE_LEVEL,
				payload: {
					tlsCertificateLevel: null,
					tlsCertificateName: null,
					tlsCertificateFilePath: null,
					tlsCertificateFileId: null,
					tlsCertificateFileHash: null,
				},
			});
		}
	}, [state.IDSelectedRestaurant]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditDeviceActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const changeHardwareId = useCallback((value: string) => {
		dispatch({
			type: EditDeviceActionTypes.CHANGE_HARDWARE_ID,
			payload: {
				hardwareId: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDeviceActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				fetchedCustomers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: TEditDeviceSelectedItem | null) => {
		dispatch({
			type: EditDeviceActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: EditDeviceActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				fetchedRestaurants: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback((value: TEditDeviceSelectedItem | null) => {
		dispatch({
			type: EditDeviceActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	}, []);

	const addBankSettingsBlock = useCallback((value: IDeviceBankSettingForEdit) => {
		dispatch({
			type: EditDeviceActionTypes.ADD_BANK_SETTINGS_BLOCK,
			payload: {
				bankSettings: value,
			},
		});
	}, []);

	const deleteBankSettingsBlock = useCallback(
		(value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.filter((el) => el.id !== value);
			dispatch({
				type: EditDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeRestaurantBankSettings = useCallback(
		(settingsId: string, value: string | null) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, restaurantBankSettingId: value } : el,
			);
			dispatch({
				type: EditDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsTerminalId = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId ? { ...el, terminalId: value } : el,
			);
			dispatch({
				type: EditDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const setFetchedRestaurantBankSettings = useCallback((value: IRestaurantBankSettings[]) => {
		dispatch({
			type: EditDeviceActionTypes.SET_FETCHED_RESTAURANT_BANK_SETTINGS,
			payload: {
				fetchedRestaurantBankSettings: value,
			},
		});
	}, []);

	const changeBankSettingsM4Login = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							m4BankSettingCredentials: {
								login: value,
								password: el.m4BankSettingCredentials?.password || null,
								isPasswordUpdated: el.m4BankSettingCredentials?.isPasswordUpdated || false,
							},
					  }
					: el,
			);
			dispatch({
				type: EditDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsM4Password = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							m4BankSettingCredentials: {
								login: el.m4BankSettingCredentials?.login || null,
								password: value,
								isPasswordUpdated: true,
							},
					  }
					: el,
			);
			dispatch({
				type: EditDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsToucanLogin = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							toucanBankSettingCredentials: {
								login: value,
								password: el.toucanBankSettingCredentials?.password || null,
								isPasswordUpdated: el.toucanBankSettingCredentials?.isPasswordUpdated || false,
							},
					  }
					: el,
			);
			dispatch({
				type: EditDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeBankSettingsToucanPassword = useCallback(
		(settingsId: string, value: string) => {
			const updatedBankSettingsBlocks = state.bankSettings.map((el) =>
				el.id === settingsId
					? {
							...el,
							toucanBankSettingCredentials: {
								login: el.toucanBankSettingCredentials?.login || null,
								password: value,
								isPasswordUpdated: true,
							},
					  }
					: el,
			);
			dispatch({
				type: EditDeviceActionTypes.SET_BANK_SETTINGS,
				payload: {
					bankSettings: updatedBankSettingsBlocks,
				},
			});
		},
		[state.bankSettings],
	);

	const changeTlsCertificateName = useCallback((value: string) => {
		dispatch({
			type: EditDeviceActionTypes.CHANGE_TLS_CERTIFICATE_NAME,
			payload: {
				tlsCertificateName: value,
			},
		});
	}, []);

	const setTlsCertificateFile = useCallback(
		({ fileId, fileHash }: { fileId: Nullable<string>; fileHash: Nullable<string> }) => {
			dispatch({
				type: EditDeviceActionTypes.SET_TLS_CERTIFICATE_FILE,
				payload: {
					tlsCertificateFileId: fileId,
					tlsCertificateFileHash: fileHash,
				},
			});
		},
		[],
	);

	const setDataForSubmit = useCallback((value: IEditDevice | null) => {
		dispatch({
			type: EditDeviceActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: EditDeviceActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<IDeviceHardwareIdValidationError> | null) => {
		dispatch({
			type: EditDeviceActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditDeviceActionTypes.RESET_FIELDS }), []);

	return (
		<EditDeviceContext.Provider
			value={{
				refetchData,
				changeHardwareId,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDRestaurant,
				addBankSettingsBlock,
				deleteBankSettingsBlock,
				changeRestaurantBankSettings,
				changeBankSettingsTerminalId,
				setFetchedRestaurantBankSettings,
				changeBankSettingsM4Login,
				changeBankSettingsM4Password,
				changeBankSettingsToucanLogin,
				changeBankSettingsToucanPassword,
				changeTlsCertificateName,
				setTlsCertificateFile,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				deviceId: state.deviceId,
				hardwareId: state.hardwareId,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				bankSettings: state.bankSettings,
				availableRestaurantBankSettings: state.availableRestaurantBankSettings,
				dataForSubmit: state.dataForSubmit,
				tlsCertificateLevel: state.tlsCertificateLevel,
				tlsCertificateName: state.tlsCertificateName,
				tlsCertificateFilePath: state.tlsCertificateFilePath,
				tlsCertificateFileId: state.tlsCertificateFileId,
				tlsCertificateFileHash: state.tlsCertificateFileHash,
				fetchedRestaurantBankSettings: state.fetchedRestaurantBankSettings,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</EditDeviceContext.Provider>
	);
};

export default EditDeviceState;
